import { defineStore } from 'pinia'
import { expectArray } from '../composables/expectShape'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'

export interface FailedJobEntry {
  class_name: string | null
  queue_name: string | null
  failed_at: string
}

export interface FailedJobsSummary {
  count: number
  jobs: FailedJobEntry[]
}

const POLL_MS = 30_000

// Drives the standing red failed-jobs banner (#1087). Unlike every other
// admin store, this one owns its own poll interval rather than relying on
// a specific view being mounted or an ActionCable channel — the banner has
// to stay accurate on every route, not just while an admin happens to be
// looking at a particular page, and there's no broadcast for Solid Queue
// failures to subscribe to. subscribe()/unsubscribe() (wired via
// useAdminSubscriptions) start/stop that interval.
export const useFailedJobsStore = defineStore('failedJobs', () => {
  let pollTimer: ReturnType<typeof setInterval> | undefined

  const {
    data: summary,
    loading,
    error: loadError,
    needsAuth,
    refresh: fetchSummary,
  } = useAsyncResource<FailedJobsSummary>(async () => {
    const res = await fetch('/api/v1/failed_jobs', { credentials: 'include' })
    if (res.status === 401) throw new UnauthorizedError()
    if (!res.ok) throw new Error(`Failed to load failed-job summary: ${res.status}`)
    const data = (await res.json()) as FailedJobsSummary
    return { ...data, jobs: expectArray<FailedJobEntry>(data.jobs, 'failed jobs') }
  })

  function subscribe(): void {
    if (pollTimer) return
    void fetchSummary().catch(() => {})
    pollTimer = setInterval(() => void fetchSummary().catch(() => {}), POLL_MS)
  }

  function unsubscribe(): void {
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = undefined
  }

  return {
    summary,
    loading,
    needsAuth,
    loadError,
    fetchSummary,
    subscribe,
    unsubscribe,
  }
})
